import {
  Alert,
  Badge,
  Button,
  Dropdown,
  Icon,
  LayoutProvider,
  type LayoutProviderProps,
  Logo,
  type SideMenuItem,
  type SideMenuLeafItem,
  Tooltip,
  type UserAction,
} from '@eleo-rh/core-ui'
import UserLabel from 'components/UserLabel'
import { API_URL_PROFILE_SWITCH, API_URL_SSO_LOGOUT } from 'constants/api'
import { API_REMINDERS_COUNT, API_USER_ROUTE_PINS } from 'constants/api-v2'
import { PAGES_WITHOUT_SIDEBAR } from 'constants/sidebar'
import { useAuth } from 'context/AuthProvider'
import { useI18n } from 'context/I18nProvider'
import { useProfile } from 'context/ProfileProvider'
import { useQuery } from 'context/QueryProvider'
import { useToast } from 'context/ToastProvider'
import { useImageUrl } from 'hooks/binary'
import { useMercure } from 'hooks/mercure'
import { useModules } from 'hooks/modules'
import useSWR from 'hooks/swr'
import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useRouteLoaderData } from 'react-router-dom'
import type { PermissionCode } from 'shared/permissions'
import type { Profile, Settings } from 'shared/types'
import { getItemPermissions, items, type LayoutSiderItem, type LayoutSiderItems } from '../layout/sider/items'
import AppsMenu from './header/AppsMenu'
import Notifications from './header/Notifications'
import TopMessage from './header/TopMessage'
import useLayoutContentHeight from './useLayoutContentHeight'

type LayoutMercureEvent =
  | {
      type: 'new_message'
      notificationType?: 'success' | 'info' | 'warning' | 'error'
      label: string
      labelParams?: Record<string, unknown> | unknown[]
    }
  | {
      type: 'reminders_count_update'
      count: number | string
    }

interface Message {
  type: 'success' | 'info' | 'warning' | 'error'
  content: string
}

function itemHasPermission(
  item: LayoutSiderItem,
  hasPermission: (permission: PermissionCode) => boolean,
  profileType: string,
  settings: Settings,
  inheritedPermissions: PermissionCode[] = [],
) {
  const itemPermissions = item.permissions?.length ? item.permissions : inheritedPermissions
  const allowedByPermission = !itemPermissions.length || itemPermissions.some(permission => hasPermission(permission))

  const allowedByFeature = profileType === 'super-admin' || !item.feature || settings.features?.[item.feature]

  return allowedByPermission && allowedByFeature
}

function stripItemPermissions(item: LayoutSiderItem): SideMenuItem {
  const {
    permissions: _permissions,
    feature: _feature,
    enabled: _enabled,
    pinnable: _pinnable,
    children,
    ...cleanItem
  } = item

  if (!children?.length) return cleanItem as SideMenuItem

  return {
    ...cleanItem,
    children: children.map(stripItemPermissions),
  } as SideMenuItem
}

function filterItemChildren(
  item: LayoutSiderItem,
  hasPermission: (permission: PermissionCode) => boolean,
  profileType: string,
  settings: Settings,
  inheritedPermissions: PermissionCode[] = [],
): LayoutSiderItem | null {
  if (item.enabled === false) return null

  const itemPermissions = item.permissions?.length ? item.permissions : inheritedPermissions
  const filteredChildren = item.children
    ?.map(child => filterItemChildren(child, hasPermission, profileType, settings, itemPermissions))
    .filter((child): child is LayoutSiderItem => child !== null)

  if (item.children?.length && !filteredChildren?.length) return null
  if (!itemHasPermission(item, hasPermission, profileType, settings, inheritedPermissions) && !filteredChildren?.length)
    return null

  return {
    ...item,
    ...(filteredChildren ? { children: filteredChildren } : {}),
  } as LayoutSiderItem
}

function normalizeMenuKey(key?: string) {
  return key?.split('?')[0].replace(/\/$/, '')
}

const PINNED_ADMIN_ROUTES_ORDER = ['/admin/training-management', '/admin/accreditation/accreditations']

function pathMatchesMenuKey(currentPath: string, menuKey: string) {
  return currentPath === menuKey || currentPath.startsWith(`${menuKey}/`)
}

function findMenuPath(items: SideMenuItem[], currentPath: string, ancestors: string[] = []): string[] | undefined {
  const matches: string[][] = []

  for (const item of items) {
    if (item.type === 'divider') {
      continue
    }

    if (item.type === 'group') {
      if ('children' in item && item.children?.length) {
        const childPath = findMenuPath(item.children as SideMenuItem[], currentPath, ancestors)
        if (childPath) matches.push(childPath)
      }
      continue
    }

    const itemKey = String(item.key ?? '')
    const nextAncestors = 'children' in item && item.children?.length && itemKey ? [...ancestors, itemKey] : ancestors

    if ('children' in item && item.children?.length) {
      const childPath = findMenuPath(item.children as SideMenuItem[], currentPath, nextAncestors)
      if (childPath) matches.push(childPath)
    }

    const normalizedKey = normalizeMenuKey(itemKey)
    if (normalizedKey && pathMatchesMenuKey(currentPath, normalizedKey)) {
      matches.push(itemKey ? [...ancestors, itemKey] : ancestors)
    }
  }

  return matches.sort((firstPath, secondPath) => {
    const firstKey = normalizeMenuKey(firstPath.at(-1)) ?? ''
    const secondKey = normalizeMenuKey(secondPath.at(-1)) ?? ''

    return secondKey.length - firstKey.length
  })[0]
}

interface CoreUILayoutProviderProps extends LayoutProviderProps {}

const CoreUILayoutProvider = ({ children, contentStyle, ...props }: CoreUILayoutProviderProps) => {
  const settings = useRouteLoaderData('root') as Settings
  const userData = useRouteLoaderData('user') as { routePins?: string[] } | undefined
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [message, setMessage] = useState<Message | null>(null)
  const [reminders, setReminders] = useState<number | null>(null)
  const [routePins, setRoutePins] = useState(() =>
    (userData?.routePins ?? []).filter(route => route.startsWith('/admin/')),
  )
  const [isUpdatingRoutePins, setIsUpdatingRoutePins] = useState(false)
  const { subscribe } = useMercure()
  const { i18n, locale: lang, setLocale, i18name } = useI18n()
  const { logout, refreshToken, user } = useAuth()
  const { profile, profiles, hasPermission } = useProfile()
  const { client } = useQuery()
  const toast = useToast()
  const [mainLogoUrl] = useImageUrl(settings.mainLogo?.url) as [string | null, unknown]
  const [reducedLogoUrl] = useImageUrl(settings.reducedLogo?.url) as [string | null, unknown]
  const perimeter = user?.perimeter
  const profileType = profile?.type ?? ''
  const modules = useModules()
  const { layoutWrapperRef, resolvedContentStyle } = useLayoutContentHeight({
    contentStyle,
    showHeader: props.showHeader,
  })

  useEffect(() => {
    if (!user) return

    const channels = ['/users', `/users/${user.id}`, ...(profile?.id ? [`/profiles/${profile.id}`] : [])]

    const topics = ['/api/2.0/messages', '/api/2.0/reminders']

    const messageEventSource = subscribe(channels, topics, ({ data }: { data: LayoutMercureEvent }) => {
      switch (data.type) {
        case 'new_message':
          setMessage({ type: data.notificationType ?? 'warning', content: i18n(data.label, data.labelParams ?? []) })
          break
        case 'reminders_count_update':
          setReminders(Number(data.count))
          break
        default:
          break
      }
    })

    return () => messageEventSource?.close()
  }, [i18n, profile?.id, subscribe, user])

  const withReminders = hasPermission('SESSION_CAN_VIEW_TASK')

  useSWR(withReminders && user ? `${API_REMINDERS_COUNT}?done=false` : null, {
    onSuccess: data => setReminders(Number(data.count)),
  })

  const handleProfileClick = (val: string | number) => {
    client
      .post(API_URL_PROFILE_SWITCH.replace(':id', String(val)))
      .then((response: { token: string }) => {
        refreshToken(response.token)
        window.location.reload()
      })
      .catch((error: unknown) => {
        toast.error(error, { title: i18n('internal.error') })
      })
  }

  const handleSwitchLocale = (val: string | number) => {
    const locale = String(val)
    window.localStorage.setItem('locale', locale)
    client.updateHeaders()
    setLocale(locale)
    return navigate(`${pathname}${search}`.replace(lang, locale))
  }

  const handleLogout = () => {
    logout()
    if (settings.ssoEnabled) {
      window.location.replace(API_URL_SSO_LOGOUT)
    } else {
      window.location.href = `/${lang}/login`
    }
  }

  const userActions: UserAction[] = [
    {
      Component: user ? <Notifications /> : null,
    },
    {
      Component:
        withReminders && user ? (
          <Badge count={reminders} offset={[-3, 3]}>
            <Button
              singleIcon="alarm-clock"
              variant="secondary"
              onClick={() => navigate(`reminders/${user.id}/dates`)}
            />
          </Badge>
        ) : null,
      separator: true,
    },
    {
      Component: (
        <div className="flex items-center gap-2 min-w-40">
          <Dropdown
            variant="borderless"
            prefix={<Icon name="language" style={{ marginRight: '8px' }} size={'lg'} />}
            value={lang}
            allowClear={false}
            popupMatchSelectWidth={false}
            showSearch={false}
            onChange={handleSwitchLocale}
            options={(settings.locales || []).map(locale => ({
              label: i18n(`locale.${locale}`),
              value: locale,
            }))}
          />
        </div>
      ),
    },
    {
      Component:
        profiles.length > 1 ? (
          <Dropdown
            variant="borderless"
            value={profile?.id}
            allowClear={false}
            popupMatchSelectWidth={false}
            showSearch={false}
            onChange={handleProfileClick}
            options={profiles.map((p: Profile) => ({
              label: i18name(p),
              value: p.id,
            }))}
          />
        ) : (
          <div>{profile ? i18name(profile) : null}</div>
        ),
      separator: true,
    },
    {
      Component: <AppsMenu />,
      separator: true,
    },
    ...(user
      ? [
          {
            Component: (
              <UserLabel
                id={user?.id}
                label={`${user?.firstname} ${user?.lastname?.toUpperCase()}`}
                withIcon={false}
                className="font-bold"
              />
            ),
          },
          {
            Component: (
              <Tooltip placement="left" title={i18n('logout')}>
                <Button variant="secondary" singleIcon="arrow-right-from-bracket" onClick={handleLogout} />
              </Tooltip>
            ),
          },
        ]
      : []),
  ]

  const menuItems = useMemo(() => {
    const { coreItems, adminItems } = items({
      tableauURL: settings.tableauURL,
      i18n,
      perimeter,
      settings,
      profileType,
      modules,
      hasPermission,
    }) as LayoutSiderItems

    const filteredCoreItems = coreItems
      .filter(item => item.enabled !== false)
      .filter(item => itemHasPermission(item, hasPermission, profileType, settings))
      .map(stripItemPermissions)

    const filteredAdminItems = adminItems
      .filter(item => item.enabled !== false)
      .map(item => filterItemChildren(item, hasPermission, profileType, settings))
      .filter((item): item is LayoutSiderItem => item !== null)
      .map(stripItemPermissions)

    const pinnableAdminRoutes = new Set(
      adminItems
        .flatMap(item => item.children ?? [])
        .filter(item => item.pinnable)
        .map(item => `/${normalizeMenuKey(String(item.key))}`),
    )

    const adminItemsByRoute = new Map(
      filteredAdminItems
        .flatMap(item => ('children' in item ? item.children : []))
        .filter(
          (item): item is SideMenuLeafItem =>
            item.type !== 'divider' &&
            !('children' in item) &&
            Boolean(item.key) &&
            pinnableAdminRoutes.has(`/${normalizeMenuKey(String(item.key))}`),
        )
        .map(item => [`/${normalizeMenuKey(String(item.key))}`, item]),
    )

    const decoratedAdminItemsByRoute = new Map(
      Array.from(adminItemsByRoute.entries()).map(([route, item]) => {
        const isPinned = routePins.includes(route)

        return [
          route,
          {
            ...item,
            label: (
              <div className="group relative min-w-0">
                <div className="pr-5">{item.label}</div>
                <Tooltip title={i18n(isPinned ? 'menu.action.admin_unpin' : 'menu.action.admin_pin')}>
                  <button
                    type="button"
                    aria-label={i18n(isPinned ? 'menu.action.admin_unpin' : 'menu.action.admin_pin')}
                    disabled={isUpdatingRoutePins}
                    className={`absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 leading-none disabled:cursor-default ${
                      isPinned
                        ? 'text-primary-50 hover:text-primary-500 active:text-primary-100'
                        : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 text-primary-600 hover:text-primary-100 active:text-primary-700'
                    }`}
                    onClick={async event => {
                      event.preventDefault()
                      event.stopPropagation()

                      const previousRoutePins = routePins
                      const nextRoutePins = isPinned
                        ? routePins.filter(routePin => routePin !== route)
                        : [...routePins, route]

                      setRoutePins(nextRoutePins)
                      setIsUpdatingRoutePins(true)

                      try {
                        const response = await client.put(API_USER_ROUTE_PINS, { routePins: nextRoutePins })
                        setRoutePins(
                          (response.routePins as string[]).filter((routePin: string) => routePin.startsWith('/admin/')),
                        )
                      } catch (error) {
                        setRoutePins(previousRoutePins)
                        toast.error(error, { title: i18n('internal.error') })
                      } finally {
                        setIsUpdatingRoutePins(false)
                      }
                    }}
                  >
                    <Icon name="thumbtack" size="xs" />
                  </button>
                </Tooltip>
              </div>
            ),
          } as SideMenuItem,
        ]
      }),
    )

    const pinnedAdminItems = [...routePins]
      .sort((firstRoute, secondRoute) => {
        const firstIndex = PINNED_ADMIN_ROUTES_ORDER.indexOf(firstRoute)
        const secondIndex = PINNED_ADMIN_ROUTES_ORDER.indexOf(secondRoute)

        return (
          (firstIndex === -1 ? PINNED_ADMIN_ROUTES_ORDER.length : firstIndex) -
          (secondIndex === -1 ? PINNED_ADMIN_ROUTES_ORDER.length : secondIndex)
        )
      })
      .flatMap(route => {
        const item = decoratedAdminItemsByRoute.get(route)
        return item ? [item] : []
      })

    const unpinnedAdminItems = filteredAdminItems
      .map(item => {
        if (!('children' in item)) return item

        const children = item.children.flatMap(child => {
          const route = `/${normalizeMenuKey(String(child.key))}`
          if (pinnableAdminRoutes.has(route) && routePins.includes(route)) return []

          return [decoratedAdminItemsByRoute.get(route) ?? child]
        })

        return children.length ? { ...item, children } : null
      })
      .filter((item): item is SideMenuItem => item !== null)

    const showAdminMenu = getItemPermissions(adminItems).some(permission => hasPermission(permission))
    return showAdminMenu && filteredAdminItems.length
      ? [
          ...filteredCoreItems,
          { type: 'divider' } as SideMenuItem,
          {
            key: 'menu.admin',
            icon: 'gear',
            label: i18n('menu.admin'),
            children: [
              ...pinnedAdminItems,
              ...(pinnedAdminItems.length ? [{ type: 'divider' } as SideMenuItem] : []),
              ...unpinnedAdminItems,
            ],
          } as SideMenuItem,
        ]
      : filteredCoreItems
  }, [client, hasPermission, modules, i18n, isUpdatingRoutePins, perimeter, profileType, routePins, settings, toast])

  const match = /(\/[a-z]{2})\/([\w]+)/.exec(pathname)
  const showSider = !(match && PAGES_WITHOUT_SIDEBAR.includes(match[2]))
  const currentPath = pathname.replace(/^\/[a-z]{2}\//, '').replace(/\/$/, '')
  const selectedPath = useMemo(() => findMenuPath(menuItems, currentPath) ?? [], [currentPath, menuItems])
  const selectedKey = selectedPath.at(-1)
  const routeOpenKeys = useMemo(() => selectedPath.slice(0, -1), [selectedPath])
  const [openKeys, setOpenKeys] = useState<string[]>([])

  useEffect(() => {
    setOpenKeys(keys =>
      routeOpenKeys.every(key => keys.includes(key)) ? keys : [...new Set([...keys, ...routeOpenKeys])],
    )
  }, [routeOpenKeys])

  return (
    <div ref={layoutWrapperRef}>
      <LayoutProvider
        showSider={showSider}
        headerProps={{
          pageTitle: message ? (
            <Alert.Inline title={message.content} status="warning" />
          ) : (
            <TopMessage settings={settings} />
          ),
          userActions,
          classNames: {
            actionsWrapper: 'gap-sm',
          },
        }}
        siderProps={{
          logo: collapsed => (
            <Link to={`/${lang}/catalog/training`} title={settings.version} aria-label={i18n('back_to_homepage')}>
              {collapsed ? (
                settings.reducedLogo ? (
                  <img
                    src={reducedLogoUrl ?? undefined}
                    alt=""
                    className={`h-10 object-contain ${collapsed ? 'max-w-10' : 'max-w-full'}`}
                  />
                ) : (
                  <Logo ghost showModuleName={false} />
                )
              ) : settings.mainLogo ? (
                <img
                  src={mainLogoUrl ?? undefined}
                  alt=""
                  className={`h-10 object-contain ${collapsed ? 'max-w-10' : 'max-w-full'}`}
                />
              ) : (
                <Logo ghost />
              )}
            </Link>
          ),
          menuProps: {
            items: menuItems,
            selectedKeys: selectedKey ? [selectedKey] : [],
            openKeys,
            onOpenChange: keys => setOpenKeys(keys as string[]),
            styles: {
              root: { paddingBottom: '8rem' },
            },
          },
          width: 272,
          logoLinkProps: {
            href: `/${lang}/catalog/training`,
            title: settings.version,
            'aria-label': i18n('back_to_homepage'),
          },
        }}
        contentStyle={resolvedContentStyle}
        {...props}
        footer={
          settings.bottomMessageEnabled ? (
            <Link
              to={`/${lang}/info-message`}
              className="flex w-full justify-center text-primary-900 underline! font-bold"
            >
              {i18n('label.footer')}
            </Link>
          ) : null
        }
      >
        {children}
      </LayoutProvider>
    </div>
  )
}

export default CoreUILayoutProvider
